/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
} from 'reactstrap';

import {
  findingGridDataRefreshAtom,
  selectedHostsAtom,
  sourceTypeConfig,
} from './state';

interface IgnoreVulnModalProps {
  finding: any;
  isOpen: boolean;
  toggle: () => void;
}

const IgnoreReasons = [
  { value: 'FALSE_POSITIVE', label: 'False Positive' },
  { value: 'RISK_ACCEPTED', label: 'Risk Accepted' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
];

const colourStyles = {
  control: (styles) => ({
    ...styles,
    // backgroundColor: 'white'
  }),
  option: (styles, {}) => {
    // const color = chroma(data.color);
    return {
      ...styles,
      textAlign: 'left',
    };
  },
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
  }),
};

const IgnoreVulnModal: React.FC<IgnoreVulnModalProps> = ({
  isOpen,
  finding,
  toggle,
}) => {
  const selectedHosts = useAtomValue(selectedHostsAtom);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);
  const [ignoredReason, setIgnoredReason] = useState<{
    value: string;
    label: string;
  }>(IgnoreReasons[0]);
  const [ignoredComments, setIgnoredComments] = useState('');
  const [selectedOption, setSelectedOption] = useState('ignore');
  const [findingIdsBySameQid, setFindingIdsBySameQid] = useState<number[]>([]);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const findingId = finding.id;
  const sourceType = finding.source_type;
  const title = finding.name;
  const qid = finding.qid;
  const ruleId = finding.sonarqube_rule;

  // eslint-disable-next-line no-console
  // console.log('selectedHosts', selectedHosts);

  // only required for CA and EP Vulnerabilities
  let hostName = finding.host_name;
  if (!hostName && sourceType === 'endpoint_data')
    hostName = finding.endpoint_ip;
  if (!hostName && sourceType === 'ca_data')
    hostName = finding.cloud_agent_name;

  useEffect(() => {
    if (sourceType === 'endpoint_data' || sourceType === 'ca_data') {
      // make an api call for this qid and host to check if there are multiple
      //  vulnerabilities by this qid and host
      let url = `findings/get_related`;
      setLoading(true);
      callApi(url, 'GET', { finding_id: findingId })
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            if (res.data.finding_ids) {
              setFindingIdsBySameQid(res.data.finding_ids);
            } else setFindingIdsBySameQid([]);
          }
        })
        .catch((err) => {
          let res = err.response;
          if (res.status === 401 || res.status === 400 || res.status === 422) {
            showFailureToast(res);
            setError(err);
          }
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReasonChange = (selectedOption: any) => {
    setIgnoredReason(selectedOption);
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIgnoredComments(e.target.value);
  };

  const handleIgnoreVuln = () => {
    if (!error) {
      // Perform ignore vulnerability logic here
      // You can access ignoredReason and ignoredComments state variables
      // make an api call for this qid and host to check if there are multiple
      //  vulnerabilities by this qid and host
      let url = `findings/ignore`;

      let data = {
        action: selectedOption === 'ignore' ? 'ignore' : 'ignore_all',
        reason: ignoredReason?.value || 'FALSE_POSITIVE',
        comments: ignoredComments,
        company_id: localStorage.getItem('currentCompanyId'),
        source_type: sourceType,
      };

      switch (selectedOption) {
        case 'ignore':
          data['finding_id'] = findingId;
          break;
        case 'ignore_all':
          if (sourceType === 'sast') {
            data['finding_id'] = findingId;
          } else {
            data['qid'] = qid;
          }
          break;
        case 'ignore_for_hosts':
          if (sourceType === 'sast') {
            data['finding_id'] = findingId;
          } else {
            data['qid'] = qid;
          }
          data['host_ids'] = selectedHosts.map((item) => item.id);
          break;
      }

      setLoading(true);
      callApi(url, 'PUT', {}, data)
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            showSuccessToast('Vulnerability Ignored Successfully');
            sourceTypeConfig[finding.source_type].onIgnoreVuln();
            refreshFindingsGridData(Math.random().toString());
            toggle();
          }
        })
        .catch((err) => {
          let res = err.response || err;
          setError(res.data.message);
          showFailureToast(res.data.message);
        })
        .finally(() => setLoading(false));
    }
  };

  const getFindingIdsBySameQid = () => {
    const firstThree = findingIdsBySameQid.slice(0, 3);

    return `${firstThree.join(', ')}${
      findingIdsBySameQid.length > 3 ? '...' : ''
    }`;
  };

  const handleCancel = () => {
    toggle();
    setIgnoredComments('');
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Ignore Vulnerability</ModalHeader>
      <ModalBody>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12">
            {' '}
            <label>
              <p>
                {' '}
                <strong>Name: </strong>
                {title}
              </p>
            </label>
          </Col>
          <Col md="6">
            <label>
              <p>
                <strong>Finding ID: </strong>
                {findingId}
              </p>
            </label>
          </Col>
          {sourceType === 'sast' ? (
            <Col md="6">
              <label>
                <p>
                  <strong>Rule ID: </strong>
                  {ruleId}
                </p>
              </label>
            </Col>
          ) : (
            <Col md="6">
              <label>
                <p>
                  <strong>QID: </strong>
                  {qid}
                </p>
              </label>
            </Col>
          )}
        </Row>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12" className="spaced">
            <section>
              <strong>
                <label style={{ marginBottom: '0px' }}>
                  <p>Ignore Options</p>
                </label>
              </strong>
              <div className="form-check ml-4">
                <input
                  className="form-check-input"
                  type="radio"
                  name="vuln_ignore_options"
                  id="vuln_ignore_options1"
                  value="ignore"
                  checked={selectedOption === 'ignore'}
                  onChange={handleOptionChange}
                />
                {(sourceType === 'endpoint_data' || sourceType === 'ca_data') &&
                findingIdsBySameQid.length > 1 ? (
                  <>
                    <label
                      className="form-check-label"
                      htmlFor="vuln_ignore_options1"
                    >
                      <p>
                        Ignore all instances of this vulnerability for{' '}
                        <strong>{hostName}</strong> host.
                      </p>
                    </label>
                    {selectedOption === 'ignore' && (
                      <label
                        className="text-warning form-check-label"
                        htmlFor="vuln_ignore_options1"
                      >
                        <strong>Warning:</strong> There are{' '}
                        <strong>{findingIdsBySameQid.length}</strong>{' '}
                        vulnerabilities with the same QID for the same Host
                        (i.e., with Finding IDs: {getFindingIdsBySameQid()}).
                        Ignoring one of these will result in all being ignored,
                        including some that might be in Fixed status.
                      </label>
                    )}
                  </>
                ) : (
                  <label
                    className="form-check-label"
                    htmlFor="vuln_ignore_options1"
                  >
                    <p>Ignore selected instance of this vulnerability.</p>
                  </label>
                )}
              </div>
              {selectedHosts?.length > 0 && (
                <div className="form-check  ml-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vuln_ignore_options"
                    id="vuln_ignore_options2"
                    value="ignore_for_hosts"
                    checked={selectedOption === 'ignore_for_hosts'}
                    onChange={handleOptionChange}
                  />
                  <>
                    <label
                      className="form-check-label"
                      htmlFor="vuln_ignore_options2"
                    >
                      <p>
                        Ignore all instances of this vulnerability for selected
                        hosts
                      </p>
                    </label>
                    {selectedOption === 'ignore_for_hosts' &&
                      (sourceType === 'endpoint_data' ||
                        sourceType === 'ca_data') && (
                        <label
                          className="text-warning form-check-label"
                          htmlFor="vuln_ignore_options1"
                        >
                          <strong>Warning:</strong> There might be multiple
                          vulnerabilities with the same QID. By bulk Ignoring
                          one of these will result in all being ignored
                          {sourceType !== 'sast' &&
                            ', including some that might be in Fixed status'}
                          .
                        </label>
                      )}
                  </>
                </div>
              )}
              {atob(localStorage.getItem(btoa('approle')) || btoa('client')) ===
                'admin' && (
                <div className="form-check ml-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vuln_ignore_options"
                    id="vuln_ignore_options3"
                    value="ignore_all"
                    checked={selectedOption === 'ignore_all'}
                    onChange={handleOptionChange}
                  />
                  <>
                    <label
                      className="form-check-label"
                      htmlFor="vuln_ignore_options3"
                    >
                      <p>
                        Ignore all instances of this vulnerability for all hosts
                      </p>
                    </label>
                    {selectedOption === 'ignore_all' &&
                      (sourceType === 'endpoint_data' ||
                        sourceType === 'ca_data') && (
                        <label
                          className="text-warning form-check-label"
                          htmlFor="vuln_ignore_options1"
                        >
                          <strong>Warning:</strong> There might be multiple
                          vulnerabilities with the same QID. By bulk Ignoring
                          one of these will result in all being ignored
                          {sourceType !== 'sast' &&
                            ', including some that might be in Fixed status'}
                          .
                        </label>
                      )}
                  </>
                </div>
              )}
            </section>
          </Col>
        </Row>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12" className="spaced">
            <strong>
              <label style={{ marginBottom: '0px' }}>
                <p>Ignore Reason</p>
              </label>
            </strong>
          </Col>
          <Col md="12" className="spaced" style={{ marginBottom: '10px' }}>
            <Select
              className="custom-360-dropdown"
              value={ignoredReason}
              onChange={handleReasonChange}
              options={IgnoreReasons}
              styles={colourStyles}
            />
          </Col>
          <Col md="12" className="spaced">
            <strong>
              <label style={{ marginBottom: '0px' }}>
                <p>Ignore Comments</p>
              </label>
            </strong>
          </Col>
          <Col md="12" className="spaced">
            <Input
              type="text"
              value={ignoredComments}
              placeholder="Comments"
              maxLength={100}
              onChange={handleCommentsChange}
            />
          </Col>
          <Col md="12" className="spaced">
            <label style={{ color: 'dodgerblue' }}>
              Note: Ignored vulnerabilities are removed from the display of open
              vulnerabilities. They can be retrieved with the “Status” filter.
              They can also be Unignored.
            </label>
            {error && (
              <label className="text-danger">
                <strong>Error:</strong> An error occurred while attempting to
                ignore. Unable to proceed.
              </label>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={!!loading}>
          Cancel
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button color="primary" onClick={handleIgnoreVuln} disabled={!!error}>
            Ignore
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default IgnoreVulnModal;
